/**
 * Post-Checkout success landing.
 *
 * The redirect from Stripe alone does NOT grant entitlement. We poll the
 * Firestore subscription mirror (the authoritative read model) until the
 * webhook flips it to trialing/active. Only then do we consider the flow
 * successful and offer a link into the app.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRight } from 'lucide-react';

import { Button } from '../components/ui/button';
import { getSubscription, statusLabel } from '../services/saasBillingService';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS        = 30; // ~60 seconds

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'polling', sub: null, attempts: 0 });
  const stoppedRef = useRef(false);

  useEffect(() => {
    stoppedRef.current = false;
    let attempts = 0;
    let timer = null;

    async function tick() {
      if (stoppedRef.current) return;
      attempts += 1;
      try {
        const data = await getSubscription();
        const s = data?.subscription?.status;
        if (s === 'trialing' || s === 'active') {
          setState({ status: 'ready', sub: data.subscription, attempts });
          return;
        }
        setState({ status: 'polling', sub: data?.subscription || null, attempts });
      } catch (_) {
        // ignore transient errors
      }
      if (attempts >= MAX_POLLS) {
        setState((prev) => ({ ...prev, status: 'timeout', attempts }));
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    }
    tick();
    return () => {
      stoppedRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const isReady   = state.status === 'ready';
  const isTimeout = state.status === 'timeout';

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-6"
         data-testid="billing-success-page">
      <div className="max-w-lg w-full rounded-2xl border border-neutral-200 bg-white p-10 text-center shadow-sm">
        {isReady ? (
          <>
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 mb-4">
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold text-neutral-900"
                data-testid="billing-success-heading">
              You&apos;re all set.
            </h1>
            <p className="mt-2 text-neutral-600">
              Your subscription is <b>{statusLabel(state.sub?.status)}</b>
              {state.sub?.planKey ? <> on the <b>{state.sub.planKey}</b> plan.</> : '.'}
            </p>
            <div className="mt-8 flex flex-col gap-2">
              <Button
                className="w-full h-11"
                onClick={() => navigate('/dashboard')}
                data-testid="billing-success-goto-dashboard-btn"
              >
                Go to dashboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Link
                to="/billing"
                className="text-sm text-neutral-500 hover:text-neutral-800 underline underline-offset-2"
                data-testid="billing-success-goto-billing-link"
              >
                View billing details
              </Link>
            </div>
          </>
        ) : isTimeout ? (
          <>
            <h1 className="text-2xl font-semibold text-neutral-900"
                data-testid="billing-success-timeout-heading">
              Still processing…
            </h1>
            <p className="mt-2 text-neutral-600">
              Payment went through, but the confirmation from Stripe is taking longer than usual.
              Your subscription will activate automatically — refresh in a minute or two.
            </p>
            <div className="mt-8 flex flex-col gap-2">
              <Button
                variant="secondary"
                className="w-full h-11"
                onClick={() => window.location.reload()}
                data-testid="billing-success-refresh-btn"
              >
                Refresh
              </Button>
              <Link
                to="/billing"
                className="text-sm text-neutral-500 hover:text-neutral-800 underline underline-offset-2"
                data-testid="billing-success-timeout-billing-link"
              >
                Go to billing
              </Link>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-neutral-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-neutral-900"
                data-testid="billing-success-polling-heading">
              Finalizing your subscription…
            </h1>
            <p className="mt-2 text-neutral-600">
              Waiting for Stripe to confirm the payment. This usually takes a couple of seconds.
            </p>
            <p className="mt-4 text-xs text-neutral-400"
               data-testid="billing-success-attempt-count">
              Attempt {state.attempts} of {MAX_POLLS}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
